import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { buildEmail } from '@adornis/mails/db/templates.js';
import { html, nothing } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import type { DateTime } from 'luxon';
import type { DurchfuehrungsTag } from '../_cms/products/entities/Product.js';
import { imprintText } from '../imprint.js';
import type { Student } from '../master-data-management/users/entities/Student.js';
import { logoImageRef } from './server/mailer.js';

export let baseMail: Parameters<typeof buildEmail>[0] | null = null;

let campusBaseMail: Parameters<typeof buildEmail>[0] | null = null;

export function initBaseMail() {
  baseMail = {
    // The logo asset is 5560x1015px. The `width` ATTRIBUTE on the <img> is what actually constrains
    // it — Outlook's Word renderer ignores CSS `max-width` — so without it the image renders at its
    // intrinsic size and drags the whole footer table off the page. `height: auto` keeps the aspect
    // ratio, and `alt` gives recipients something readable while remote content is still blocked
    // (Thunderbird and Outlook block it by default). Same approach the header logo in
    // @adornis/mails uses: a width attribute plus `height: auto`.
    get footer() {
      return html`
        <table cellspacing="0" style="width: 100%;">
          <tr>
            <td style="width: 40% !important; vertical-align: middle; padding: 8px;">
              <img
                src=${A.absoluteUrl(logoImageRef)}
                alt="Chancenwerk e.V."
                width="200"
                style="width: 200px; max-width: 100%; height: auto; display: block; border: 0;"
              />
            </td>
            <td style="padding: 8px; vertical-align: center; text-align: right;"><p>Chancenwerk e.V.</p></td>
          </tr>
          <tr>
            <td colspan="2" style="padding: 8px;">
              <p style="line-height: 1.3em;">${imprintText}</p>
            </td>
          </tr>
          <tr>
            <td style="padding: 8px;">
              <p>
                <a href=${A.absoluteUrl('/privacy')}> Datenschutzerklärung </a>
              </p>
            </td>
          </tr>
        </table>
      `;
    },
    colorConfig: { highlight: '#AFCA00', mainBack: '#ffffff', wrapperBack: '#f6f6f6', mainBorder: '#eeeeee' },
    customStyle: '.logo { background: #AFCA00; }',
    contents: '',
  };

  campusBaseMail = {
    ...baseMail,
    get footer() {
      return baseMail?.footer ?? '';
    },
    get logoConfig() {
      return { src: A.absoluteUrl(logoImageRef), alt: 'ChancenCampus Logo' };
    },
  };
}

export const registrationConfirmationSelfService = ({ confirmationUrl }: { confirmationUrl: string }) => {
  if (!campusBaseMail) throw new Error('Campus base mail not initialized');
  return buildEmail({
    ...campusBaseMail,
    title: 'Erfolgreich bei ChancenCampus registriert!',
    contents: html`Sie haben sich erfolgreich registriert. Bitte bestätigen Sie ihre E-Mail indem Sie diesen Link
      aufrufen: <a href=${confirmationUrl}>Hier klicken!</a>
      <br />
      <br />
      Viele Grüße, Ihr ChancenCampus-Team`,
  });
};

export const changePasswordMessage = ({ confirmationUrl }: { confirmationUrl: string }) => {
  if (!campusBaseMail) throw new Error('Campus base mail not initialized');
  return buildEmail({
    ...campusBaseMail,
    title: 'Du hast dein Passwort vergessen?',
    contents: html`Du kannst dein Passwort ändern indem du: <a href=${confirmationUrl}>Hier klickst!</a>
      <br />
      <br />
      Viele Grüße, Ihr ChancenCampus-Team`,
  });
};

export const registrationInformationUSForAdmin = ({
  studentPrename,
  studentLastname,
  studentSchool,
  studentID,
  studentEmail,
  studentGrade,
  studentPhone,
}: {
  studentPrename: string;
  studentLastname: string;
  studentSchool: string;
  studentID: string;
  studentEmail: string;
  studentPhone: string;
  studentGrade: string;
}) => {
  if (!campusBaseMail) throw new Error('Campus base mail not initialized');
  return buildEmail({
    ...campusBaseMail,
    title: 'Neue Anmeldung im ChancenRaum',
    contents: html`Hallo,
      <br />
      <br />
      folgendes Kind wurde über das digitale Anmeldeformular neu im CHANCENraum angelegt:
      <br />
      <ul>
        <li>Vorname: ${studentPrename}</li>
        <li>Nachname: ${studentLastname}</li>
        <li>Schule: ${studentSchool}</li>
        <li>Jahrgangsstufe: ${studentGrade}</li>
        <li>Telefonnummer: ${studentPhone}</li>
        <li>E-Mail: ${studentEmail}</li>
        <li>ID: ${studentID}</li>
      </ul>
      <br />
      <br />
      @Zuständige:r MA + SchuKo: Wenn der Prozess funktioniert hat, dann solltet Ihr bereits mit den Eltern gesprochen
      haben (und Ihnen im Nachgang den Link zur Anmeldung zugeschickt haben). Sollte euch der Name des Kindes nichts
      sagen (und die Eltern den Link zur Anmeldung ggf. über einen anderen Weg erhalten haben), dann kontaktiert die
      Eltern bitte nun so schnell wie möglich, um das Erstgespräch nachzuholen!
      <br />
      <br />
      Wenn ihr allgemeine Fragen zum CHANCENraum habt, dann meldet euch gerne unter
      <a href="mailTo:verwaltung@chancenwerk.org">verwaltung@chancenwerk.org</a>
      <br />
      <br />
      Viele Grüße
      <br />
      Euer CHANCENraum-Team`,
  });
};

export const registrationInformationOSForAdmin = ({
  studentPrename,
  studentLastname,
  studentSchool,
  studentID,
  chosenIntensiveCourses,
  formOfMembership,
}: {
  studentPrename: string;
  studentLastname: string;
  studentSchool: string;
  studentID: string;
  chosenIntensiveCourses: DurchfuehrungsTag[] | undefined;
  formOfMembership: string;
}) => {
  if (!baseMail) throw new Error('Campus base mail not initialized');
  return buildEmail({
    ...baseMail,
    title: 'Neue Anmeldung im ChancenRaum',
    contents: html`Hallo,
      <br />
      <br />
      Hallo, folgende/r Jugendliche:r wurde über das digitale Anmeldeformular neu im CHANCENraum angelegt:
      <br />
      <ul>
        <li>Vorname: ${studentPrename}</li>
        <li>Nachname: ${studentLastname}</li>
        <li>Schule: ${studentSchool}</li>
        <li>ID: ${studentID}</li>
        <li>Form der Mitgliedschaft: ${formOfMembership}</li>
      </ul>
      <br />
      ${chosenIntensiveCourses?.length
        ? html`Gewählte IK vor-Ort:
            <ul>
              ${chosenIntensiveCourses.map(
                course => html`
                  <li>
                    ${course.wochentag}, ${course.startZeit?.toFormat('hh:mm')} - ${course.endZeit?.toFormat('hh:mm')},
                    ${course.locations.length > 1 ? 'Räume' : 'Raum'}: ${course.locations.join(', ')}
                  </li>
                `,
              )}
            </ul>`
        : html`<span>Es wurden keine vor-Ort IK Termine ausgewählt.</span><br />`}
      <br />
      <br />
      @Zuständige:r MA + OS-Coach: Bitte meldet euch vor der Kick-Off-Veranstaltung, Schnupper-Lernförderung oder dem
      ersten Workshop bei dem OS unter den angegebenen Kontaktdaten, damit der OS daran erinnert wird und zu den
      Terminen erscheint.
      <br />
      <br />
      Wenn ihr allgemeine Fragen zum CHANCENraum habt, dann meldet euch gerne unter
      <a href="mailTo:verwaltung@chancenwerk.org">verwaltung@chancenwerk.org</a>
      <br />
      <br />
      Viele Grüße
      <br />
      Euer CHANCENraum-Team`,
  });
};

export const registrationInterestOS = ({
  studentPrename,
  studentLastname,
  studentContact,
  choosenSubjects,
  formOfMembership,
}: {
  studentPrename: string;
  studentLastname: string;
  studentContact: string;
  choosenSubjects: string[];
  formOfMembership: string;
}) => {
  if (!baseMail) throw new Error('Campus base mail not initialized');
  return buildEmail({
    ...baseMail,
    title: 'Neue Interessensbekundung',
    contents: html`Hallo,
      <br />
      <br />
      Gute Nachrichten!
      <br />
      <br />
      Folgende jugendliche Person hat Interesse an der CHANCENreise teilzunehmen:
      <br />
      <br />
      ${studentPrename} ${studentLastname}
      <br />
      <br />
      <li>Kontakt: ${studentContact}</li>
      <li>Form der Mitgliedschaft: ${formOfMembership}</li>
      <br />
      <br />
      Gewählte Fächer: ${choosenSubjects.join(', ')}`,
  });
};

export const registrationInterestAcquisitionOS = ({
  studentPrename,
  studentLastname,
  studentSchool,
  studentPhone,
  studentEmail,
}: {
  studentPrename: string;
  studentLastname: string;
  studentSchool: string;
  studentPhone: string;
  studentEmail: string;
}) => {
  if (!baseMail) throw new Error('Campus base mail not initialized');
  return buildEmail({
    ...baseMail,
    title: 'Neue Interessensbekundung OS-Akquise',
    contents: html`Hallo,
      <br />
      <br />
      Hallo, folgende/r Jugendliche:r hat Interesse an der CHANCENreise:
      <br />
      <br />
      Vorname: ${studentPrename}<br />
      Nachname: ${studentLastname}<br />
      Schule: ${studentSchool}<br />
      Telefonnummer: ${studentPhone}<br />
      E-Mail: ${studentEmail}
      <br />
      <br />
      Wenn ihr allgemeine Fragen zum CHANCENraum habt, dann meldet euch gerne unter verwaltung@chancenwerk.org
      <br />
      <br />
      Viele Grüße Euer CHANCENraum-Team`,
  });
};

export const updateInformationOSForAdmin = ({
  studentPrename,
  studentLastname,
  studentSchool,
  studentID,
  chosenIntensiveCourses,
}: {
  studentPrename: string;
  studentLastname: string;
  studentSchool: string;
  studentID: string;
  chosenIntensiveCourses: DurchfuehrungsTag[] | undefined;
}) => {
  if (!baseMail) throw new Error('Campus base mail not initialized');
  return buildEmail({
    ...baseMail,
    title: 'Neue IK im ChancenRaum gewählt',
    contents: html`Hallo,
      <br />
      <br />
      Hallo, folgende/r Jugendliche:r hat über den Campus digitale IK ausgewählt:
      <br />
      <ul>
        <li>Vorname: ${studentPrename}</li>
        <li>Nachname: ${studentLastname}</li>
        <li>Schule: ${studentSchool}</li>
        <li>ID: ${studentID}</li>
      </ul>
      <br />
      ${chosenIntensiveCourses?.length
        ? html`Gewählte IK vor-Ort:
            <ul>
              ${chosenIntensiveCourses.map(
                course => html`
                  <li>
                    ${course.wochentag}, ${course.startZeit?.toFormat('hh:mm')} - ${course.endZeit?.toFormat('hh:mm')},
                    ${course.locations.length > 1 ? 'Räume' : 'Raum'}: ${course.locations.join(', ')}
                  </li>
                `,
              )}
            </ul>`
        : html`<span
              >Es wurden keine vor-Ort IK Termine ausgewählt. Möglicherweise wurden vorherige vor-Ort IK
              abgewählt.</span
            ><br />`}
      <br />
      Wenn ihr allgemeine Fragen zum CHANCENraum habt, dann meldet euch gerne unter
      <a href="mailTo:verwaltung@chancenwerk.org">verwaltung@chancenwerk.org</a>
      <br />
      <br />
      Viele Grüße
      <br />
      Euer CHANCENraum-Team`,
  });
};

export const registrationInformationOfExternalTeacherForAdmin = ({
  teacherPrename,
  teacherLastname,
  teacherEmail,
  teacherSchool,
  kpfLink,
}: {
  teacherPrename: string;
  teacherEmail: string;
  teacherLastname: string;
  teacherSchool: string;
  kpfLink: string;
}) => {
  if (!campusBaseMail) throw new Error('Campus base mail not initialized');
  return buildEmail({
    ...campusBaseMail,
    title: 'Neue Anmeldung im ChancenRaum',
    contents: html`Hallo,
      <br />
      <br />
      Eine Lehrkraft hat einen Account für den CHANCENcampus beantragt.
      <br />
      <ul>
        <li>Vorname: ${teacherPrename}</li>
        <li>Nachname: ${teacherLastname}</li>
        <li>E-Mail: ${teacherEmail}</li>
        <li>Schule: ${teacherSchool}</li>
      </ul>
      @Zuständige:r MA + SchuKo: Der angefragte Account ist zurzeit deaktiviert und muss manuell freigeschaltet werden.
      Die Freischaltung ist im CHANCENraum in der Übersicht des KPFs unter dem Reiter "Zugangs-Code für Lehrkräfte" zu
      finden. Hier ist der Link zur KPF-Verwaltung <a href=${kpfLink}>${teacherSchool}</a>.
      <br />
      <br />
      <br />
      <br />
      Viele Grüße
      <br />
      Euer ChancenRaum-Team`,
  });
};

export const kpfTokenCreationInformation = ({
  tokenSection,
  token,
  usage,
}: {
  tokenSection: string;
  token: string;
  usage: string;
}) => {
  if (!campusBaseMail) throw new Error('Campus base mail not initialized');
  return buildEmail({
    ...campusBaseMail,
    title: `Der Zugangscode für den CHANCENcampus für ${tokenSection} wurde angelegt`,
    contents: html`Hallo,
      <br />
      <br />
      soeben wurde der Zugangscode zum CHANCENcampus für ${tokenSection} Ihrer Schule angelegt.

      <br />
      <br />
      Der Zugangscode lautet: ${token}
      <br />
      ${tokenSection === 'Schüler:innen'
        ? html`
            Anzahl der ${tokenSection}-Accounts, die mit diesem Code erstellt werden können: ${usage ? `${usage}` : ''}
            <br />
            <br />
          `
        : html``}
      ${tokenSection === 'Schüler:innen'
        ? html`
            Die Schüler:innen erreichen den CHANCENcampus unter
            <a href="www.chancencampus.org">www.chancencampus.org</a>. Dort können sie "Account anlegen" klicken und
            dort dann den Zugangscode eingeben. Nach Eingabe der Daten erhalten die Schüler:innen eine Mail an die
            eingetragene Mail-Adresse und können sich unmittelbar ihren Account für den CHANCENcampus anlegen.
          `
        : html`
            Sie erreichen den CHANCENcampus unter <a href="www.chancencampus.org">www.chancencampus.org</a>. Dort können
            Sie "Account anlegen" klicken und dort dann den Zugangscode eingeben. Die Registrierung wird im Nachgang von
            unserem Verwaltungsteam geprüft. Sobald Ihre Anfrage angenommen wurde, werden Sie per E-Mail benachrichtigt.
          `}
      <br />
      <br />
      Viele Grüße
      <br />
      Ihr Team von Chancenwerk e.V.`,
  });
};

export const kpfTokenChangeInformation = ({ tokenSection, token }: { tokenSection: string; token: string }) => {
  if (!campusBaseMail) throw new Error('Campus base mail not initialized');
  return buildEmail({
    ...campusBaseMail,
    title: `Der Zugangscode für den CHANCENcampus für ${tokenSection} hat sich geändert`,
    contents: html`Hallo,
      <br />
      <br />
      soeben wurde der Zugangscode zum CHANCENcampus für ${tokenSection} Ihrer Schule geändert. Dieser Code ist nur
      relevant, um neue Accounts anzulegen. Für alle ${tokenSection}, die bereits einen Account haben, ändert sich
      nichts.
      <br />
      <br />
      Der neue Zugangscode lautet: ${token}
      <br />
      <br />
      Viele Grüße
      <br />
      Ihr Team von Chancenwerk e.V.`,
  });
};

//! TODO: adjust Template to fit registration for ChancenCampus with token
export const registerAtChancenCampusWithToken = ({
  username,
  confirmationUrl,
}: {
  username: string;
  confirmationUrl: string;
}) => {
  if (!campusBaseMail) throw new Error('Campus base mail not initialized');
  return buildEmail({
    ...campusBaseMail,
    title: 'Willkommen im ChancenCampus',
    contents: html`Liebe Eltern, liebe Schüler:innen,
      <br />
      <br />
      Ihr Kind hat den Zugang zum CHANCENcampus über die Schule bekommen. Vielen Dank, dass Sie Ihr Kind registriert
      haben.
      <br />
      <br />
      Der CHANCENcampus ist die Online-Lernplattform von Chancenwerk e.V. Ihr Kind kann die Plattform zusätzlich zu den
      Lernheften nutzen. Die Nutzung ist freiwillig.
      <br />
      <br />
      Mit dem CHANCENcampus können Schüler:innen digital lernen. Es gibt zum Beispiel Tests, Rätsel und Lernvideos. Das
      Angebot steht erst am Anfang und wird nach und nach erweitert, sodass wir von Chancenwerk Schüler:innen noch
      besser unterstützen können.
      <br />
      <br />
      Mit dieser E-Mail schicken wir Ihnen die Login-Daten für den CHANCENcampus.
      <br />
      <br />
      Sie erreichen den CHANCENcampus unter <a href=${'https://www.chancencampus.org'}>www.chancencampus.org</a>.
      <br />
      <br />
      Mit dieser E-Mail teilen wir Ihnen ihr Login-Kürzel mit und einen Link mit dem Sie ihr Passwort festlegen können.
      <br />
      <br />
      Kürzel: ${username}
      <br />
      Hier können sie ihr Passwort wählen: <a href=${confirmationUrl}>LINK</a>
      <br />
      <br />
      <b>
        Das Passwort muss mindestens aus 8 Zeichen bestehen, einen Großbuchstaben, eine Zahl und ein Sonderzeichen
        enthalten.
      </b>
      <br />
      <br />
      Technische Fragen zum CHANCENcampus beantworten wir unter
      <br />
      chancencampus-support@chancenwerk.org.
      <br />
      <br />
      Viele Grüße
      <br />
      Ihr Team von Chancenwerk e.V.`,
  });
};

export const registerAtChancenCampus = ({
  username,
  confirmationUrl,
}: {
  username: string;
  confirmationUrl: string;
}) => {
  if (!campusBaseMail) throw new Error('Campus base mail not initialized');
  return buildEmail({
    ...campusBaseMail,
    title: 'Willkommen im ChancenCampus',
    contents: html`Liebe Eltern, liebe Schüler:innen,
      <br />
      <br />
      da Ihr Kind bei Chancenwerk e.V. angemeldet ist, darf es ab sofort auch unsere Lernplattform, den CHANCENcampus,
      nutzen. Für die Nutzung dieses Angebots entstehen Ihnen keine zusätzlichen Kosten. Die Nutzung des CHANCENcampus
      ist freiwillig.
      <br />
      <br />
      Mit dem CHANCENcampus können Schüler:innen zusätzlich digital lernen. Es gibt zum Beispiel Tests, Rätsel und
      Lernvideos. Das Angebot steht erst am Anfang und wird nach und nach erweitert, sodass Chancenwerk Schüler:innen
      noch besser unterstützen kann.
      <br />
      <br />
      Mit dieser E-Mail schicken wir Ihnen die Login-Daten für den CHANCENcampus.
      <br />
      <br />
      Sie erreichen den CHANCENcampus unter www.chancencampus.org. Dort können Sie sich mit ihrem Kürzel einloggen und
      ein Passwort festlegen.
      <br />
      <br />
      Kürzel: ${username}
      <br />
      <a href=${confirmationUrl}>Hier können sie ihr Passwort wählen</a>
      <br />
      <br />
      <b>
        Das Passwort muss mindestens aus 8 Zeichen bestehen, einen Großbuchstaben, eine Zahl und ein Sonderzeichen
        enthalten.
      </b>
      <br />
      <br />
      Technische Fragen zum CHANCENcampus beantworten wir unter
      <br />
      chancencampus-support@chancenwerk.org.
      <br />
      <br />
      Viele Grüße
      <br />
      Ihr Team von Chancenwerk e.V.`,
  });
};

export const registerPasswordSetAtChancenCampus = ({
  username,
  confirmationUrl,
}: {
  username: string;
  confirmationUrl: string;
}) => {
  if (!campusBaseMail) throw new Error('Campus base mail not initialized');
  return buildEmail({
    ...campusBaseMail,
    title: 'Willkommen im ChancenCampus',
    contents: html`Liebe Eltern, liebe Schüler:innen,
      <br />
      <br />
      da Ihr Kind bei Chancenwerk e.V. angemeldet ist, darf es ab sofort auch unsere Lernplattform, den CHANCENcampus,
      nutzen. Für die Nutzung dieses Angebots entstehen Ihnen keine zusätzlichen Kosten. Die Nutzung des CHANCENcampus
      ist freiwillig.
      <br />
      <br />
      Mit dem CHANCENcampus können Schüler:innen zusätzlich digital lernen. Es gibt zum Beispiel Tests, Rätsel und
      Lernvideos. Das Angebot steht erst am Anfang und wird nach und nach erweitert, sodass Chancenwerk Schüler:innen
      noch besser unterstützen kann.
      <br />
      <br />
      Mit dieser E-Mail informieren wir Sie darüber, dass ein Campus-Zugang unter dem Kürzel ${username} für Ihr Kind
      eingerichtet worden ist. Das Passwort wurde in der Lernförderung festgelegt.
      <br />
      <br />
      Sie erreichen den CHANCENcampus unter <a href="https://www.chancencampus.org">www.chancencampus.org</a>.
      <br />
      <br />
      Dort können Sie sich mit ihrem Kürzel und dem ausgewählten Passwort einloggen
      <br />
      <br />
      Fragen zum CHANCENcampus beantworten wir unter
      <a href="mailto:chancencampus-support@chancenwerk.org">chancencampus-support@chancenwerk.org</a>.
      <br />
      <br />
      Sollten Sie nicht wollen, dass ihr Kind den CHANCENcampus nutzt, können Sie der Nutzung widersprechen und wir
      löschen den Account. Um dies zu veranlassen können Sie sich unter
      <a href="mailto:verwaltung@chancenwerk.org">verwaltung@chancenwerk.org</a> bei uns melden.
      <br />
      <br />
      Viele Grüße
      <br />
      Ihr Team von Chancenwerk e.V.`,
  });
};

export const registerExternalTeacher = ({
  username,
  confirmationUrl,
}: {
  username: string;
  confirmationUrl: string;
}) => {
  if (!campusBaseMail) throw new Error('Campus base mail not initialized');
  return buildEmail({
    ...campusBaseMail,
    title: 'Willkommen im ChancenCampus',
    contents: html`Liebe Lehrkraft,
      <br />
      <br />
      im Rahmen unseres Lernförderangebots gibt es ab sofort auch unsere digitale Lernplattform, den CHANCENcampus. Die
      Schüler:innen können diesen als freiwilliges Zusatzangebot nutzen.
      <br />
      <br />
      Mit dem CHANCENcampus können Schüler:innen zusätzlich zur Lernförderung digital lernen. In den digitalen Inhalten
      finden sich z. B. Tests, Rätsel und Lernvideos. Auch die QR-Codes in unseren Lernheften führen zu ergänzenden
      CHANCENhefte auf dem CHANCENcampus. Das Angebot steht erst am Anfang und wird nach und nach erweitert.
      <br />
      <br />
      Mit deinem Zugang als Lehrkraft kannst du schauen, welche CHANCENhefte deinen Schüler:innen zur Verfügung stehen
      und bspw. die Auswertung der Diagnoseergebnisse einsehen. Mehr Informationen dazu findest du in den
      Prozessbeschreibungen und Handreichungen zum CHANCENcampus und zu unserer Lernverlaufsdiagnostik, der
      CHANCENkurve.
      <br />
      <br />
      Mit dieser E-Mail schicken wir dir deine Login-Daten für den CHANCENcampus.
      <br />
      <br />
      Du erreichst den CHANCENcampus unter www.chancencampus.org. Dort kannst du dich mit deiner
      E-Mail/Nutzer:innennamen einloggen und ein Passwort festlegen.
      <br />
      <br />
      Nutzer:innennamen: ${username}
      <br />
      <a href=${confirmationUrl}>Hier kannst du dein Passwort wählen</a>
      <br />
      <br />
      <b>
        Das Passwort muss mindestens aus 8 Zeichen bestehen, einen Großbuchstaben, eine Zahl und ein Sonderzeichen
        enthalten.
      </b>
      <br />
      <br />
      Technische Fragen zum CHANCENcampus beantworten wir gerne unter:
      <br />
      chancencampus-support@chancenwerk.org.
      <br />
      <br />
      Viele Grüße
      <br />
      Euer Team von Chancenwerk e.V.`,
  });
};

export const registerEmployee = ({ username, confirmationUrl }: { username: string; confirmationUrl: string }) => {
  if (!campusBaseMail) throw new Error('Campus base mail not initialized');
  return buildEmail({
    ...campusBaseMail,
    title: 'Willkommen im ChancenCampus',
    contents: html`Liebe:r Chancenwerker:in,
      <br />
      <br />
      im Rahmen unseres Lernförderangebots gibt es ab sofort auch unsere digitale Lernplattform, den CHANCENcampus. Die
      Schüler:innen können diesen als freiwilliges Zusatzangebot nutzen.
      <br />
      <br />
      Mit dem CHANCENcampus können Schüler:innen zusätzlich zur Lernförderung digital lernen. In den digitalen Inhalten
      finden sich z. B. Tests, Rätsel und Lernvideos. Auch die QR-Codes in unseren Lernheften führen zu ergänzenden
      CHANCENhefte auf dem CHANCENcampus. Das Angebot steht erst am Anfang und wird nach und nach erweitert.
      <br />
      <br />
      Mit deinem Zugang als Übungsleitung kannst du zum einen schauen, welche CHANCENhefte deinen Schüler:innen zur
      Verfügung stehen und bspw. die Auswertung der Diagnoseergebnisse einsehen. Mehr Informationen dazu findest du in
      den Prozessbeschreibungen und Handreichungen zum CHANCENcampus und zu unserer Lernverlaufsdiagnostik, der
      CHANCENkurve.
      <br />
      <br />
      Zum anderen ermöglicht dir dein Zugang als Übungsleitung auch die Möglichkeit, dich zu den tollen Veranstaltungen
      der CHANCENakademie anzumelden. Klicke dafür auf der Homepage
      <a href="https://chancenwerk.de/was-wir-machen/chancenakademie/"
        >https://chancenwerk.de/was-wir-machen/chancenakademie/</a
      >
      auf den Anmeldebutton der Veranstaltung, die du besuchen möchtest, gib deine E-Mail-Adresse an und wähle einen
      Termin aus. Eine detailliertere Beschreibung rund um die Anmeldungen und Veranstaltung der CHANCENakademie findest
      du in der Toolbox
      <a href="https://drive.google.com/drive/folders/1MzV6WLB7_z-9INlTX1_nY_WTvy9240p2"
        >https://drive.google.com/drive/folders/1MzV6WLB7_z-9INlTX1_nY_WTvy9240p2</a
      >.
      <br />
      <br />
      Mit dieser E-Mail schicken wir dir deine Login-Daten für den CHANCENcampus.
      <br />
      <br />
      Du erreichst den CHANCENcampus unter www.chancencampus.org. Dort kannst du dich mit deinem Kürzel einloggen und
      ein Passwort festlegen.
      <br />
      <br />
      Kürzel: ${username}
      <br />
      <a href=${confirmationUrl}>Hier kannst du dein Passwort wählen</a>
      <br />
      <br />
      <b>
        Das Passwort muss mindestens aus 8 Zeichen bestehen, einen Großbuchstaben, eine Zahl und ein Sonderzeichen
        enthalten.
      </b>
      <br />
      <br />
      Technische Fragen zum CHANCENcampus beantworten wir gerne unter:
      <br />
      chancencampus-support@chancenwerk.org.
      <br />
      <br />
      Viele Grüße
      <br />
      Euer Team von Chancenwerk e.V.`,
  });
};

export const registerEmployeeOnboarding = ({ vorname, onboardingUrl }: { vorname: string; onboardingUrl: string }) => {
  if (!baseMail) throw new Error('Campus base mail not initialized');
  return buildEmail({
    ...baseMail,
    title: 'Willkommen bei Chancenwerk – Deine Vereinbarungsunterlagen',
    contents: html`Liebe:r ${vorname},
      <br />
      <br />
      herzlich willkommen bei Chancenwerk e.V.! Wir freuen uns, dass du bei Chancenwerk als Übungsleiter:in beginnst.
      <br />
      <br />
      Um deinen Einstieg abzuschließen, bitten wir dich, deine Vereinbarungsunterlagen digital zu bestätigen. Bitte
      klicke auf den folgenden Link, um deine Unterlagen einzusehen:
      <br />
      <br />
      <a href=${onboardingUrl}>Hier klicken, um die Vereinbarungsunterlagen einzusehen</a>
      <br />
      <br />
      Du wirst automatisch zu deinen Vereinbarungsunterlagen weitergeleitet. Dort kannst du:
      <br />
      <ul>
        <li>Deine Vereinbarung einsehen und bestätigen</li>
        <li>Deine Stammdaten überprüfen und ergänzen</li>
        <li>Die Datenschutzerklärung und Selbstverpflichtungserklärung zum Kinderschutzkonzept bestätigen</li>
        <li>Die Datenschutzerklärung zur Masernimpfung bestätigen</li>
      </ul>
      <br />
      Wichtig: Deine Tätigkeit kann erst nach der Bestätigung aller genannten Unterlagen beginnen! Zudem übersenden wir
      Dir im Nachgang die Bestätigung des Arbeitgebers für Deinen Antrag eines erweiterten Führungszeugnisses. Das
      Führungszeugnis erhältst Du damit kostenfrei und sollst uns das innerhalb von 4 Wochen per Kopie oder Scan (an
      verwaltung@chancenwerk.org) nachreichen.
      <br />
      <br />
      Der Link ist 14 Tage gültig. Falls er abgelaufen ist, wende dich bitte an die Verwaltung unter
      <a href="mailto:verwaltung@chancenwerk.org">verwaltung@chancenwerk.org</a>. An diese Adresse kannst Du dich auch
      bei allen sonstigen Fragen wenden.
      <br />
      <br />
      Viele Grüße
      <br />
      Dein Team von Chancenwerk e.V.`,
  });
};

export const onboardingConfirmation = ({
  vorname,
  passwordUrl,
  username,
}: {
  vorname: string;
  passwordUrl: string;
  username: string;
}) => {
  if (!baseMail) throw new Error('Campus base mail not initialized');
  return buildEmail({
    ...baseMail,
    title: 'Willkommen bei Chancenwerk – Deine nächsten Schritte',
    contents: html`Liebe:r ${vorname},
      <br />
      <br />
      vielen Dank, dass du deine Vereinbarungsunterlagen bestätigt hast! Hier sind deine nächsten Schritte:
      <br />
      <br />
      <b>1. Führungszeugnis beantragen</b>
      <br />
      Du erhältst die Bestätigung des Arbeitgebers für deinen Antrag eines erweiterten Führungszeugnisses in Kürze per
      Post von uns zugesandt. Die Bestätigung ist zur Vorlage bei der zuständigen Behörde bestimmt. Damit bekommst Du
      das erweiterte Führungszeugnis kostenfrei. Bitte stelle uns dein Führungszeugnis postalisch als Kopie oder Scan
      (an verwaltung@chancenwerk.org) innerhalb der nächsten vier Wochen zur Verfügung. Wir werden die Kopie oder den
      Scan im Nachgang vernichten. Solltest Du noch ein erweitertes Führungszeugnis besitzen, darf dies nicht älter als
      drei Monate sein.
      <br />
      <br />
      Tipp: Du kannst dein Führungszeugnis auch online beim Bundesjustizministerium unter folgendem Link beantragen:
      <br />
      <a href="https://www.fuehrungszeugnis.bund.de/">Führungszeugnis online beantragen</a>
      <br />
      <br />
      <b>2. CHANCENcampus – Konto erstellen & Passwort setzen</b>
      <br />
      Klicke auf den folgenden Link, um dein Passwort für den CHANCENcampus festzulegen:
      <br />
      <a href=${passwordUrl}>Passwort festlegen</a>
      <br />
      <br />
      Als Login-Namen kannst du entweder deine bei uns hinterlegte E-Mail-Adresse oder deine ID (${username}) nutzen. Du
      erreichst den CHANCENcampus unter www.chancencampus.org.
      <br />
      <br />
      Mit dem CHANCENcampus können Schüler:innen zusätzlich zur Lernförderung digital lernen. In den digitalen Inhalten
      finden sich z. B. Tests, Rätsel und Lernvideos. Auch die QR-Codes in unseren Lernheften führen zu ergänzenden
      CHANCENheften auf dem CHANCENcampus. Das Angebot steht erst am Anfang und wird nach und nach erweitert.
      <br />
      <br />
      Mit deinem Zugang als Übungsleitung kannst du zum einen schauen, welche CHANCENhefte deinen Schüler:innen zur
      Verfügung stehen und bspw. die Auswertung der Diagnoseergebnisse einsehen. Mehr Informationen dazu findest du in
      den Prozessbeschreibungen und Handreichungen zum CHANCENcampus und zu unserer Lernverlaufsdiagnostik, der
      CHANCENkurve.
      <br />
      <br />
      <b>3. Veranstaltungen der CHANCENakademie</b>
      <br />
      Dein Zugang als Übungsleitung ermöglicht dir dich zu den tollen Veranstaltungen der CHANCENakademie anzumelden.
      Klicke dafür auf der Homepage
      <a href="https://chancenwerk.de/was-wir-machen/chancenakademie/" target="_blank"
        >chancenwerk.de/chancenakademie</a
      >
      auf den Anmeldebutton der Veranstaltung, die du besuchen möchtest, gib deine E-Mail-Adresse an und wähle einen
      Termin aus. Eine detailliertere Beschreibung rund um die Anmeldungen und Veranstaltung der CHANCENakademie findest
      du in der Toolbox
      <a href="https://drive.google.com/drive/folders/1MzV6WLB7_z-9INlTX1_nY_WTvy9240p2" target="_blank"
        >CHANCENakademie Toolbox</a
      >.
      <br />
      <br />
      Bei Fragen wende dich gerne an:
      <br />
      <a href="mailto:verwaltung@chancenwerk.org">verwaltung@chancenwerk.org</a>
      <br />
      <br />
      Viele Grüße
      <br />
      Dein Team von Chancenwerk e.V.`,
  });
};

//! TODO: adjust Template
export const registerWithAccessToken = ({
  username,
  confirmationUrl,
}: {
  username: string;
  confirmationUrl: string;
}) => {
  if (!campusBaseMail) throw new Error('Campus base mail not initialized');
  return buildEmail({
    ...campusBaseMail,
    title: 'Willkommen im ChancenCampus',
    contents: html`Sehr geehrte/r Mitarbeiter:in,
      <br />
      <br />
      wir freuen uns, Ihnen mitteilen zu können, dass Ihr Zugang zum CHANCENcampus eingerichtet wurde. Ihre Zugangsdaten
      lauten wie folgt:
      <br />
      <br />
      Kürzel: ${username}
      <br />
      <a href=${confirmationUrl}>Hier können Sie ihr Passwort wählen</a>
      <br />
      <br />
      <b>
        Das Passwort muss mindestens aus 8 Zeichen bestehen, einen Großbuchstaben, eine Zahl und ein Sonderzeichen
        enthalten.
      </b>
      <br />
      <br />
      Wenn Sie Probleme beim Einloggen in die Plattform haben oder Unterstützung benötigen, wenden Sie sich bitte an:
      <br />
      chancencampus-support@chancenwerk.org.
      <br />
      <br />
      Viele Grüße
      <br />
      Ihr Team von Chancenwerk e.V.`,
  });
};

export const notifyWithNewLogins = ({ username }: { username: string }) => {
  if (!campusBaseMail) throw new Error('Campus base mail not initialized');
  return buildEmail({
    ...campusBaseMail,
    title: 'Neue Logins im ChancenCampus',
    contents: html`Liebe Eltern, liebe Schüler:innen,
      <br />
      <br />
      Die Logins im ChancenCampus haben sich geändert.
      <br />
      <br />
      Sie erreichen den CHANCENcampus unter www.chancencampus.org und können sich dort mit Ihrem folgenden neuen
      Nutzer:innennamen einloggen.
      <br />
      <br />
      Nutzer:innenname: ${username}
      <br />
      <br />
      Sollten Sie technische Fragen zum CHANCENcampus haben, können sie eine Mail an
      chancencampus-support@chancenwerk.org schicken.
      <br />
      <br />
      Viele Grüße
      <br />
      Ihr Team von Chancenwerk e.V.`,
  });
};

export const registrationConfirmation = (days: DurchfuehrungsTag[]) => {
  if (!campusBaseMail) throw new Error('Campus base mail not initialized');
  return buildEmail({
    ...campusBaseMail,
    title: 'Anmeldung zur Lernkaskade',
    contents: html`Liebe Eltern,
      <br />
      <br />
      vielen Dank für die Anmeldung zur Lernkaskade von Chancenwerk!
      <br />
      <br />
      Anbei finden Sie die Übersicht über die von Ihnen eingetragenen Daten und die Informationen zum Widerrufsrecht.
      Ihr Kind kann ab sofort an unserer Lernförderung teilnehmen.
      <br />
      <br />
      Die Lernförderung findet zu folgenden Zeiten statt: <br />
      ${days.map(
        day =>
          html`<b>${day.wochentag}</b> von <b>${day.startZeit?.toFormat('HH:mm')}</b> bis
            <b>${day.endZeit?.toFormat('HH:mm')}</b> Uhr / Raum: ${day.locations.join(', ')} <br />`,
      )}
      <br />
      <br />
      Sollten Sie noch Fragen zum weiteren Ablauf oder zur Anmeldung haben, können Sie sich gerne unter
      <a href="mailto:verwaltung@chancenwerk.org">verwaltung@chancenwerk.org</a> bei uns melden.
      <br />
      <br />
      Viele Grüße
      <br />
      Ihr Chancenwerk-Team`,
  });
};

export const registrationGraduationConfirmation = () => {
  if (!campusBaseMail) throw new Error('Campus base mail not initialized');
  return buildEmail({
    ...campusBaseMail,
    title: 'Anmeldung zum CHANCENabschluss',
    contents: html`Hallo!
      <br />
      <br />
      Hiermit bestätigen wir dir deine Anmeldung zum CHANCENabschluss von Chancenwerk. Wir freuen uns, dich bei deiner
      Vorbereitung auf die Abschlussprüfungen zu unterstützen!
      <br />
      <br />
      Bevor die Prüfungsvorbereitungskurse starten, senden wir dir das Startdatum, die Durchführungszeit und den Link
      zur Videokonferenz per Mail zu.
      <br />
      <br />
      Solltest du doch kein Interesse mehr haben, schreib uns eine kurze Mail. Vielen Dank!
      <br />
      <br />
      Bei Fragen melde dich gerne bei uns unter:
      <a href="mailto:chancenabschluss@chancenwerk.org">chancenabschluss@chancenwerk.org</a>
      <br />
      <br />
      Viele Grüße
      <br />
      Dein Team von Chancenwerk e.V.`,
  });
};

export const registerUSManualWelcomeMail = ({
  school,
  days,
  schukoHandy,
  schukoEmail,
}: {
  school: string;
  days: DurchfuehrungsTag[];
  schukoHandy: string;
  schukoEmail: string;
}) => {
  if (!campusBaseMail) throw new Error('Campus base mail not initialized');
  return buildEmail({
    ...campusBaseMail,
    title: 'Herzlich Willkommen bei Chancenwerk e.V.',
    contents: html`<h2>Herzlich Willkommen bei Chancenwerk e.V.</h2>
      <br />
      <br />
      <b>Die Anmeldung Ihres Kindes ist erfolgt!</b>
      <br />
      <br />
      Wir freuen uns, dass wir Ihr Kind in der Lernförderung von Chancenwerk beim Lernen unterstützen können.
      Chancenwerk hilft Ihrem Kind 2 x wöchentlich in Mathe, Deutsch und Englisch. Die Lernförderung von Chancenwerk
      findet direkt an der Schule statt.
      <br />
      <br />
      Lernförderzeiten an der <b>${school}</b> <br />
      ${days.map(
        day =>
          html`<b>${day.wochentag}</b> von <b>${day.startZeit?.toFormat('HH:mm')}</b> bis
            <b>${day.endZeit?.toFormat('HH:mm')}</b> Uhr / Raum: ${day.locations.join(', ')} <br />`,
      )}
      <br />
      <br />
      Bitte entschuldigen Sie Ihr Kind bei Abwesenheit, z.B. bei Krankheit rechtzeitig bei der Schulkoordination. Auch
      wenn Sie Ihr Kind im Sekretariat krankgemeldet haben, müssen Sie es zusätzlich bei Chancenwerk abmelden!
      <br />
      <br />
      <b>Kontakt Schulkoordination:</b>
      <br />
      Telefonisch während der Lernförderzeiten: ${schukoHandy} <br />
      Außerhalb der Betreuungszeiten können Sie eine SMS oder eine EMail schreiben: ${schukoEmail}
      <br />
      <br />
      <b>CHANCENhefte</b><br />
      Wir unterstützen in Fragen zu aktuellen Unterrichtsinhalten und zu anstehenden Klassenarbeiten und Tests. Deswegen
      ist es wichtig, dass Ihr Kind auch die eigenen Lernmaterialien mit in die Lernförderung bringt. Ihr Kind bekommt
      von uns auch eigenes Lernmaterial, mit dem es lernen kann.
      <br />
      <br />
      <b>CHANCENcampus</b><br />
      Zudem findet Ihr Kind auf unserer digitalen Lernplattform <b>CHANCENcampus</b>
      weiterführende Aufgaben, Erklärvideos und Lernspiele, die ein individuelles Lernen ermöglichen. So kann Ihr Kind
      auch in den Ferien oder in der Freizeit auf den CHANCENcampus zugreifen und unabhängig von der Lernförderung
      lernen. Die Zugangsdaten wird Ihr Kind in der Lernförderung von unserer Schulkoordination erhalten.
      <br />
      <br />
      Mit freundlichen Grüßen
      <br />
      Ihr Chancenwerk-Team`,
  });
};

export const accessTokenUsageInfoExternal = ({
  accessTokenUsageCurrent,
  accessTokenUsageLimit,
  accessToken,
  percentage,
  kpfShort,
}: {
  accessTokenUsageCurrent: number;
  accessTokenUsageLimit: number;
  accessToken: string;
  percentage: string;
  kpfShort: string;
}) => {
  if (!campusBaseMail) throw new Error('Campus base mail not initialized');
  return buildEmail({
    ...campusBaseMail,
    title: `Der Zugangscode für Ihre Schule hat ${percentage}% der Nutzung erreicht`,
    contents: html`Hallo,
      <br />
      <br />
      der Zugangscode für Ihre Schule hat ${percentage}% der Nutzung erreicht.
      <br />
      <br />
      Zugangscode: ${accessToken}
      <br />
      KPF: ${kpfShort}
      <br />
      Nutzung: ${accessTokenUsageCurrent} / ${accessTokenUsageLimit} Registrierungen
      <br />
      <br />
      Viele Grüße
      <br />
      Ihr Team von Chancenwerk e.V.`,
  });
};

export const accessTokenUsageInfoInternal = ({
  accessTokenUsageCurrent,
  accessTokenUsageLimit,
  accessToken,
  kpfShort,
  kpfId,
  percentage,
}: {
  accessTokenUsageCurrent: number;
  accessTokenUsageLimit: number;
  accessToken: string;
  kpfShort: string;
  kpfId: string;
  percentage: string;
}) => {
  if (!campusBaseMail) throw new Error('Campus base mail not initialized');
  return buildEmail({
    ...campusBaseMail,
    title: `Zugangscode für ${kpfShort} hat ${percentage}% der Nutzung erreicht`,
    contents: html`Hallo Tobias,
      <br />
      <br />
      der Zugangscode für ${kpfShort} hat ${percentage}% der Nutzung erreicht.
      <br />
      <br />
      Zugangscode: ${accessToken}
      <br />
      KPF: <a href=${A.absoluteUrl() + 'edit/kpf/' + kpfId}> ${kpfShort} </a>
      <br />
      Nutzung: ${accessTokenUsageCurrent} / ${accessTokenUsageLimit} Registrierungen
      <br />
      <br />
      Viele Grüße
      <br />
      Ihr Team von Chancenwerk e.V.`,
  });
};

export const workshopRegistrationEmail = ({
  name,
  workshopName,
  date,
  end,
  infos,
  managementURL,
}: {
  name: string;
  workshopName: string;
  date: DateTime;
  end: DateTime;
  infos: string;
  managementURL: Maybe<string>;
}) => {
  if (!campusBaseMail) throw new Error('Campus base mail not initialized');
  return buildEmail({
    ...campusBaseMail,
    title: 'Anmeldung bei einem Workshop',
    contents: html`Hallo,
      <br />
      <br />
      du hast dich für den Workshop <b>${workshopName}</b>, am <b>${date.toFormat('dd.MM.yyyy')}</b> von
      <b>${date.toFormat('HH:mm')}</b> bis <b>${end.toFormat('HH:mm')}</b> angemeldet. Wir freuen uns, dass du dabei
      bist!
      <br />
      <br />
      ${managementURL
        ? html`Um deine Anmeldung zu verwalten, klicke bitte auf folgenden Link:
            <a href="${managementURL}">${managementURL}</a>
            <br />
            <br />`
        : nothing}
      <b>Infos zum Termin:</b>
      <br />
      ${unsafeHTML(infos)}
      <br />
      <br />
      Von Herz zu Herz,
      <br />
      Deine CHANCENakademie`,
  });
};

export const workshopInfoEmail = ({
  name,
  workshopName,
  date,
  end,
  infos,
}: {
  name: string;
  workshopName: string;
  date: DateTime;
  end: DateTime;
  infos: string;
}) => {
  if (!campusBaseMail) throw new Error('Campus base mail not initialized');
  return buildEmail({
    ...campusBaseMail,
    title: 'Anmeldung bei einem Workshop',
    contents: html`Hallo,
      <br />
      <br />
      du hast dich für den Workshop <b>${workshopName}</b>, am <b>${date.toFormat('dd.MM.yyyy')}</b> von
      <b>${date.toFormat('HH:mm')}</b> bis <b>${end.toFormat('HH:mm')}</b> angemeldet. Wir lassen dir hiermit weitere
      Infos zum Termin zukommen.
      <br />
      <br />
      <b>Infos zum Termin:</b>
      <br />
      ${unsafeHTML(infos)}
      <br />
      <br />
      Von Herz zu Herz,
      <br />
      Deine CHANCENakademie`,
  });
};

export const workshopReminderEmail = ({
  name,
  workshopName,
  date,
  end,
  infos,
}: {
  name: string;
  workshopName: string;
  date: DateTime;
  end: DateTime;
  infos: string;
}) => {
  if (!campusBaseMail) throw new Error('Campus base mail not initialized');
  return buildEmail({
    ...campusBaseMail,
    title: 'Erinnerung an Workshop',
    contents: html`Hallo,
      <br />
      <br />
      die Veranstaltung <b>${workshopName}</b> für die du dich angemeldet hast, beginnt in Kürze. Wir freuen uns bereits
      darauf, dich bald begrüßen zu dürfen!
      <br />
      <br />
      <b>${workshopName}</b> findet am <b>${date.toFormat('dd.MM.yyyy')}</b> von <b>${date.toFormat('HH:mm')}</b> bis
      <b>${end.toFormat('HH:mm')}</b> statt. Bitte stelle sicher, dass deine Kamera und dein Mikrophon funktionieren und
      sei pünktlich. Bei einem Zuspätkommen von über 10 Minuten ist eine Teilnahme nicht mehr möglich.
      <br />
      <br />
      Falls du doch nicht teilnehmen kannst, dann sei bitte fair und storniere dein Ticket. Unentschuldigtes Fehlen wird
      von uns dokumentiert und an deine vorgesetzte Person weitergeleitet.
      <br />
      <br />
      <b>Infos zum Termin:</b>
      <br />
      ${unsafeHTML(infos)}
      <br />
      <br />
      Von Herz zu Herz,
      <br />
      Deine CHANCENakademie`,
  });
};

export const workshopStornoEmail = ({
  name,
  workshopName,
  date,
  end,
}: {
  name: string;
  workshopName: string;
  date: DateTime;
  end: DateTime;
}) => {
  if (!campusBaseMail) throw new Error('Campus base mail not initialized');
  return buildEmail({
    ...campusBaseMail,
    title: 'Stornierung eines Workshops',
    contents: html`Hallo,
      <br />
      <br />
      du hast dich für den Workshop <b>${workshopName}</b>, am <b>${date.toFormat('dd.MM.yyyy')}</b> von
      <b>${date.toFormat('HH:mm')}</b> bis <b>${end.toFormat('HH:mm')}</b> angemeldet. Wir bedauern es, dass du nicht
      teilnehmen kannst. Hiermit bestätigen wir die Stornierung deiner Anmeldung.
      <br />
      <br />
      Von Herz zu Herz,
      <br />
      Deine CHANCENakademie`,
  });
};

export const logistikOrderReminderEmail = ({ orderNumber }: { orderNumber: string }) => {
  if (!baseMail) throw new Error('Campus base mail not initialized');
  return buildEmail({
    ...baseMail,
    title: `Erinnerung zur Bearbeitung der Bestellung ${orderNumber}`,
    contents: html`Hallo, <br />
      <br />
      dies ist eine automatische Erinnerung zur Bearbeitung der Bestellung Nr. ${orderNumber}. <br />
      <br />
      Von Herz zu Herz,
      <br />
      Deine CHANCENlogistik `,
  });
};

export const workTrackingCreatedEmail = ({ link, date }: { link: string; date: DateTime }) => {
  if (!campusBaseMail) throw new Error('Campus base mail not initialized');
  return buildEmail({
    ...campusBaseMail,
    title: `Tätigkeitserfassung für Monat ${date.toFormat('MM')}. ist bereit`,
    contents: html`Hallo,
      <br />
      <br />
      deine Tätigkeitserfassung für den Monat ${date.toFormat('MM')} ist bereit. Bitte schau dir die Tätigkeitserfassung
      an und achte darauf, dass alle Tätigkeiten korrekt erfasst sind.
      <br />
      <br />
      Link: <a href=${link}>Tätigkeitserfassung</a>
      <br />
      <br />
      Viele Grüße
      <br />
      Ihr Team von Chancenwerk e.V.`,
  });
};

export const workTrackingSingleCreatedEmail = ({
  name,
  link,
  date,
}: {
  name: string;
  link: string;
  date: DateTime;
}) => {
  if (!campusBaseMail) throw new Error('Campus base mail not initialized');
  return buildEmail({
    ...campusBaseMail,
    title: `Einzeltätigkeit für Monat ${date.toFormat('MM')}. muss überprüft werden`,
    contents: html`Hallo,
      <br />
      <br />
      in der Tätigkeitserfassung für den Monat ${date.toFormat('MM')} wurde eine Einzeltätigkeit für '${name}' erfasst.
      Bitte schau dir die Tätigkeitserfassung an und achte darauf, dass alle Tätigkeiten korrekt erfasst sind.
      <br />
      <br />
      Link: <a href=${link}>Tätigkeitserfassung</a>
      <br />
      <br />
      Viele Grüße
      <br />
      Ihr Team von Chancenwerk e.V.`,
  });
};

export const workTrackingSchukoEmail = ({ name, link, date }: { name: string; link: string; date: DateTime }) => {
  if (!campusBaseMail) throw new Error('Campus base mail not initialized');
  return buildEmail({
    ...campusBaseMail,
    title: `Tätigkeit für Monat ${date.toFormat('MM')}. muss überprüft werden`,
    contents: html`Hallo,
      <br />
      <br />
      in der Tätigkeitserfassung für den Monat ${date.toFormat('MM')} wurde eine Tätigkeit für '${name}' erfasst. Bitte
      schau dir die Tätigkeitserfassung an und achte darauf, dass alle Tätigkeiten korrekt erfasst sind.
      <br />
      <br />
      Link: <a href=${link}>Tätigkeitserfassung</a>
      <br />
      <br />
      Viele Grüße
      <br />
      Ihr Team von Chancenwerk e.V.`,
  });
};

export const simpleMail = (title: string, content: Renderable) => {
  if (!campusBaseMail) throw new Error('Campus base mail not initialized');
  return buildEmail({ ...campusBaseMail, title, contents: content });
};

export const hefteContactRequestEmail = ({
  schoolName,
  code,
  email,
  tel,
}: {
  schoolName: string;
  code: number;
  email: Maybe<string>;
  tel: Maybe<string>;
}) => {
  if (!campusBaseMail) throw new Error('Campus base mail not initialized');
  return buildEmail({
    ...campusBaseMail,
    title: `Anfrage von ${schoolName} (${code})`,
    contents: html`<p>
        Liebe CHANCENlogistik, ich möchte hiermit eine Anfrage für die Lieferung von CHANCENheften stellen. Ich benötige
        Informationen zu der Verfügbarkeit und den Lieferzeiten.
      </p>
      <br />
      ${email ? html`<p>Email: <a href="mailto:${email}">${email}</a></p>` : nothing}
      ${tel ? html`<p>Telefonnummer: ${tel}</p>` : nothing}
      <br />
      Viele Grüße
      <br />
      Ihr Team von Chancenwerk e.V.`,
  });
};

export const raumIntensiveCourseStudentChangedEmail = ({
  student,
  link,
  schuelertyp,
  mitgliedschaft,
}: {
  student: NarrowedEntity<Student, { vorname: 1; nachname: 1; studentID: 1 }> | Student;
  link: string;
  schuelertyp: { old: Maybe<string>; new: Maybe<string> };
  mitgliedschaft: { old: Maybe<string>; new: Maybe<string> };
}) => {
  if (!campusBaseMail) throw new Error('Campus base mail not initialized');
  return buildEmail({
    ...campusBaseMail,
    title: `Änderung ${student.vorname} ${student.nachname} (${student.studentID})`,
    contents: html`
      <p><a href="${link}">${student.vorname} ${student.nachname} (${student.studentID})</a> wurde geändert.</p>
      <br />
      ${schuelertyp.old !== schuelertyp.new
        ? html` <p><b>Unter-/Oberstufe</b> wurde geändert von "${schuelertyp.old}" zu "${schuelertyp.new}".</p> `
        : nothing}
      ${mitgliedschaft.old !== mitgliedschaft.new
        ? html`
            <p><b>Form der Mitgliedschaft</b> wurde geändert von "${mitgliedschaft.old}" zu "${mitgliedschaft.new}".</p>
          `
        : nothing}

      <br />
      Viele Grüße
      <br />
      Ihr Team von Chancenwerk e.V.
    `,
  });
};
